import { useCurrentPage } from "@/lib/context/current-page-context";
import { useQueryClient } from "@tanstack/react-query";
import { ContentBlock } from "@/block-system/types/blocks";
import { useCallback } from "react";

/**
 * Determines if a block uses the specified table and returns the block ID if it does
 */
function getBlockIdWithMatchingTable({
  block,
  tableId,
}: {
  block: ContentBlock;
  tableId: string;
}): string | null | undefined {
  switch (block.type) {
    case "table-listing-block":
      return block.config?.tableId === tableId ? block.config.id : null;

    case "form-block": {
      if (
        block.config?.storageType === "tables" &&
        block.config?.storageId === tableId
      ) {
        return block.config.id ?? null;
      }
      return null;
    }

    case "kanban-block":
      return block.config?.tableId === tableId ? block.config.id : null;

    case "checklist-block":
      return block.config?.tableId === tableId ? block.config.id : null;

    default:
      return null;
  }
}

/**
 * Recursively collects invalidation promises for blocks that use the specified table
 */
function collectInvalidationPromises({
  block,
  tableId,
  queryClient,
}: {
  block: ContentBlock;
  tableId: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<void>[] {
  const promises: Promise<void>[] = [];

  // Recursively collect promises from children
  if ("children" in block && block.children && Array.isArray(block.children)) {
    block.children.forEach((childBlock) => {
      promises.push(
        ...collectInvalidationPromises({
          block: childBlock,
          tableId,
          queryClient,
        })
      );
    });
  }

  // Add invalidation promise for current block if it matches
  const block_id = getBlockIdWithMatchingTable({ block, tableId });
  if (block_id) {
    const invalidationPromise = queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey;

        if (queryKey.length < 3) {
          return false;
        }

        if (queryKey[0] !== "get") {
          return false;
        }

        /**
         * We could derive the `table_id` from the query key, but
         * we decided to derive it from the block configuration as it is more flexible.
         */
        if (
          !(queryKey[1] as string).includes(
            "/{block_id}/tables/{table_id}/records"
          )
        ) {
          return false;
        }

        const path = queryKey[2] as {
          params: { path: { block_id: string } };
        };

        return path.params.path.block_id === block_id;
      },
    });

    promises.push(invalidationPromise);
  }

  return promises;
}

/**
 * Hook that returns a function to invalidate all blocks using a specific table
 * This replaces the need to manually track which queries to invalidate by
 * looking at all blocks on the current page and invalidating those that use the table.
 *
 * This approach automatically handles:
 * - Table blocks (table-listing-block)
 * - Checklist blocks (checklist-block)
 */
export const useInvalidateBlocksUsingTable = () => {
  const page = useCurrentPage();

  const queryClient = useQueryClient();

  return useCallback(
    /**
     * This function assumes that blocks data is fetched with a request path that ends with `/{block_id}/tables/{table_id}/records`
     *
     * @param tableId
     * @returns Promise that resolves when all invalidations are complete
     */
    async (tableId: string): Promise<void> => {
      const allPromises = page.content.blocks.flatMap((block) =>
        collectInvalidationPromises({ block, tableId, queryClient })
      );

      await Promise.all(allPromises);
    },
    [page, queryClient]
  );
};
